import ImgWithSignedUrl from "components/SignedUrl/ImgWithSignedUrl";
import Spinner from "components/Spinner";
import { useEffect, useRef, useState } from "react";
import authHeader from "services/auth-header";
import API from "services/axios";
import LocationEmailTemplateFields, {
  applyApiTemplatesToLocationData,
  getTemplateApiPayloadList,
  stripLocationTemplateFields,
  validateLocationEmailTemplates,
} from "./locationEmailTemplateFields";

function LocationsUpdate({ data, showUpdatePage, refreshFunc }) {
  const user = JSON.parse(localStorage.getItem("user"));
  /*   All States Below
   ********************************************* */
  const [formError, setFormError] = useState({});
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const submitActionRef = useRef("save");

  const [files, setFiles] = useState({});

  /*   All Functions
   ********************************************* */
  const handleFileChange = (e) => {
    let file = e.target.files[0];

    let path = {
      panelLogo: `kms/companyLogos/${user?.companyId}/locationLogo`,
      pdfLogo: `kms/companyLogos/${user?.companyId}/locationLogo/pdfLogo`,
    };

    let fileData = {
      file: file,
      fileName: file.name,
      folder: path[e.target.name],
    };

    setFiles((prev) => {
      return { ...prev, [e.target.name]: fileData };
    });
  };

  const updateLocationsData = async (e) => {
    e.preventDefault();
    setFormError({});
    setLoading(true);

    if (!formData.locationName?.trim()) {
      setFormError({
        locationName: {
          type: "locationName",
          message: "Brand name is required",
        },
      });
      setLoading(false);
      return;
    }

    const { isValid: templatesValid, errors: templateErrors } =
      validateLocationEmailTemplates(formData);

    if (!templatesValid) {
      const messages = Object.values(templateErrors)
        .flatMap((e) => Object.values(e))
        .join("\n");
      alert(`Please fix the following before saving:\n\n${messages}`);
      setLoading(false);
      return;
    }

    const formDataValue = new FormData();

    const updatedData = stripLocationTemplateFields({
      ...formData,
      id: data.id,
    });

    const templatePayloadList = getTemplateApiPayloadList(formData);

    Array.from(Object.keys(files)).map((key) => {
      formDataValue.append(`file`, files[key].file);
    });

    formDataValue.append("id", data?.id);
    formDataValue.append("filesData", JSON.stringify(files));
    formDataValue.append("updatedData", JSON.stringify(updatedData));

    try {
      const { data: responseData } = await API.patch(
        "/locations/",
        formDataValue,
        {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        },
      );

      if (responseData.message.type === "success") {
        await Promise.all(
          templatePayloadList.map((payload) =>
            API.post(
              `/locations/${formData?.id || data?.id}/templates`,
              payload,
              {
                headers: authHeader(),
              },
            ),
          ),
        );

        refreshFunc();
        setLoading(false);

        if (submitActionRef.current === "save-close") {
          showUpdatePage(false);
        }
        return;
      }
      alert(responseData);
      setLoading(false);
    } catch (err) {
      alert(err.message);
      setLoading(false);
    }
  };

  const updateInput = (e) => {
    setFormData((prev) => {
      return { ...prev, [e.target.name]: e.target.value };
    });
  };

  useEffect(() => {
    let isMounted = true;

    const loadLocationDataAndTemplates = async () => {
      if (!data) {
        setFormData({});
        return;
      }

      const baseData = applyApiTemplatesToLocationData(data, []);
      setFormData(baseData);

      if (!data?.id) {
        return;
      }

      try {
        const response = await API.get(`/locations/${data.id}/templates`, {
          headers: authHeader(),
        });

        if (!isMounted) {
          return;
        }

        setFormData(
          applyApiTemplatesToLocationData(data, response?.data?.data || []),
        );
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setFormData(baseData);
      }
    };

    loadLocationDataAndTemplates();

    return () => {
      isMounted = false;
    };
  }, [data]);

  return (
    <div className="row">
      <div className="col-12">
        {loading && <Spinner />}
        {/*    Update FORM START
         ********************************************* */}
        {data && (
          <form className="ps-3 pe-3" onSubmit={updateLocationsData}>
            <div className="mb-3">
              <h3>Update {data?.locationName?.toUpperCase()}</h3>
            </div>
            {/* ----------  Fields  ----------- */}
            <div className="mb-3">
              <label htmlFor="locationName" className="form-label">
                Brand Name
              </label>
              <input
                className="form-control"
                type="text"
                id="locationName"
                defaultValue={data?.locationName}
                placeholder="Enter brand name"
                name="locationName"
                onChange={updateInput}
              />
              {formError && formError.locationName && (
                <p className="text-danger">{formError.locationName.message}</p>
              )}
            </div>
            <div className="mb-3">
              <label htmlFor="companyName" className="form-label">
                Company Name
              </label>
              <input
                className="form-control"
                type="text"
                id="companyName"
                placeholder="Enter company name"
                defaultValue={data?.companyName}
                name="companyName"
                onChange={updateInput}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="logo" className="form-label">
                Pdf Logo ( W x H : 200px x 100px )
              </label>
              <div className="d-flex justify-content-between">
                <div className="d-flex flex-grow-1">
                  <input
                    className="form-control"
                    type="file"
                    id="pdfLogo"
                    name="pdfLogo"
                    onChange={handleFileChange}
                  />
                </div>
                <div className="ms-2">
                  {formData?.logo && (
                    <ImgWithSignedUrl
                      path={formData?.pdfLogo}
                      styles={{ height: 35 }}
                    />
                  )}
                </div>
              </div>
            </div>

            <LocationEmailTemplateFields
              formData={formData}
              setFormData={setFormData}
            />

            <div className={`mt-4 mb-2 d-flex justify-content-end`}>
              <div>
                <button
                  className="btn px-1 btn-secondary"
                  type="button"
                  onClick={() => {
                    showUpdatePage(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success ms-1"
                  type="submit"
                  onClick={() => {
                    submitActionRef.current = "save";
                  }}
                >
                  Update
                </button>
                <button
                  className="btn btn-primary ms-1"
                  type="submit"
                  onClick={() => {
                    submitActionRef.current = "save-close";
                  }}
                >
                  Update and Close
                </button>
              </div>
            </div>
          </form>
        )}
        {/*    Update FORM END
         ********************************************* */}
      </div>
    </div>
  );
}

export default LocationsUpdate;
